import { useCallback, useEffect, useState } from "react";
import { Form } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiSave } from "react-icons/fi";
import { IoMdArrowBack } from "react-icons/io";
import { toast } from "react-toastify";
import {
  createLabTestLookup,
  getLabTestLookups,
  updateLabTestLookup,
} from "../../../../Service/labTests";
import Loader from "../../../../reusableUI/loader";
import {
  flattenLabTestLookups,
  LabTestEditFields,
  toFormValues,
  toPayload,
} from "./labTestShared";

const LabTestEdit = ({ create = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isCreate = create || !id;
  const [form] = Form.useForm();
  const [record, setRecord] = useState(location.state?.labTest || null);
  const [loading, setLoading] = useState(!isCreate && !location.state?.labTest);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadRecord = useCallback(async () => {
    if (isCreate) {
      form.setFieldsValue(toFormValues());
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await getLabTestLookups();
      if (response?.error !== false) {
        throw new Error(response?.message || "Unable to load lab test.");
      }

      const found = flattenLabTestLookups(response).find(
        (test) => String(test?._id) === String(id),
      );
      if (!found) throw new Error("Lab test not found.");
      setRecord(found);
      form.setFieldsValue(toFormValues(found));
    } catch (loadError) {
      setError(
        loadError?.response?.data?.message ||
          loadError?.message ||
          "Unable to load lab test.",
      );
    } finally {
      setLoading(false);
    }
  }, [form, id, isCreate]);

  useEffect(() => {
    if (isCreate) {
      form.setFieldsValue(toFormValues());
      setLoading(false);
      return;
    }

    if (record) form.setFieldsValue(toFormValues(record));
  }, [form, isCreate, record]);

  useEffect(() => {
    if (!isCreate && !record) loadRecord();
  }, [isCreate, loadRecord, record]);

  const handleSubmit = async (values) => {
    setSaving(true);
    setError("");
    try {
      const response = isCreate
        ? await createLabTestLookup(toPayload(values))
        : await updateLabTestLookup(id, toPayload(values));

      if (response?.error !== false || !response?.data) {
        throw new Error(response?.message || "Unable to save lab test.");
      }

      const saved = response.data;
      toast.success(isCreate ? "Lab test added successfully" : "Lab test updated successfully");
      navigate(`/admin/lab-tests/${saved._id}`, {
        replace: true,
        state: { labTest: saved },
      });
    } catch (saveError) {
      const message =
        saveError?.response?.data?.message ||
        saveError?.message ||
        "Unable to save lab test.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (isCreate) {
      navigate("/admin/lab-tests");
    } else {
      navigate(`/admin/lab-tests/${id}`, { state: { labTest: record } });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <Loader loading={loading} />
      </div>
    );
  }

  if (!isCreate && error && !record) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center bg-[#F5F7FB] px-4 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button type="button" className="mt-4 rounded-lg bg-[#4951EC] px-4 py-2 text-sm text-white shadow-sm transition hover:bg-[#3942D4]" onClick={loadRecord}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-hidden bg-[#F5F7FB]">
      <div className="h-full overflow-y-auto overscroll-contain px-2 pb-8 sm:px-3">
        <div className="sticky top-0 z-30 -mx-2 border-b border-[#E4E8F1] bg-[#F5F7FB]/95 px-2 pt-2 backdrop-blur sm:-mx-3 sm:px-3">
          <div className="mx-auto mb-3 flex max-w-[1500px] items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#E0E4F0] bg-white text-[#374151] shadow-sm transition-colors hover:border-[#C8CEFF] hover:bg-[#F0F2FF]"
                aria-label="Back"
                onClick={goBack}
              >
                <IoMdArrowBack size={20} color="#111827" />
              </button>
              <h2 className="truncate text-[18px] font-poppins font-semibold text-[#1F2937] sm:text-[22px]">
                {isCreate ? "Add Lab Test" : `Edit ${record?.parameterName || "Lab Test"}`}
              </h2>
            </div>
            <button
              type="button"
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-xl bg-[#4951EC] px-3 text-[12px] font-poppins font-medium text-white shadow-[0_6px_14px_rgba(73,81,236,0.22)] transition-colors hover:bg-[#3942D4] disabled:cursor-not-allowed disabled:opacity-60 sm:px-4"
              disabled={saving}
              onClick={() => form.submit()}
            >
              <FiSave size={14} />
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div className="mx-auto mt-4 max-w-[1500px] overflow-hidden rounded-2xl border border-[#E4E8F1] bg-white shadow-[0_12px_32px_rgba(31,41,55,0.06)]">
          {error && (
            <p role="alert" className="mx-4 mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs text-red-700 sm:mx-6">
              {error}
            </p>
          )}
          <Form form={form} layout="vertical" onFinish={handleSubmit} preserve>
            <LabTestEditFields />
            <div className="sticky bottom-0 z-20 flex flex-col-reverse gap-3 border-t border-[#E4E8F1] bg-white/95 px-5 py-4 backdrop-blur sm:flex-row sm:justify-end sm:px-8">
              <button
                type="button"
                className="h-10 rounded-xl border border-[#D9DEEB] px-5 text-[12px] font-medium text-[#4B5563] transition hover:border-[#B9C1D4] hover:bg-[#F8FAFF]"
                disabled={saving}
                onClick={goBack}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="h-10 rounded-xl bg-[#4951EC] px-5 text-[12px] font-medium text-white shadow-[0_6px_14px_rgba(73,81,236,0.2)] transition hover:bg-[#3942D4] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Lab Test"}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LabTestEdit;
